import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { truncateTo2dp } from 'utils/helpers/general';
import { convertKpaToPsi, convertCentToFahr } from 'utils/helpers/unitSystem';
import { TireStatus, determineTireStatus, getTpmsTireColor } from 'utils/tpms';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';
import { getDynamicAssetTireInfo, getOrderedTireCodes } from '../util';
import TerrainFourWheels from 'views/DriverBehavior/Inclinometer/Schematics/FourWheels';
import TerrainSixWheels from 'views/DriverBehavior/Inclinometer/Schematics/SixWheels';
import TerrainFourSixWheels from 'views/DriverBehavior/Inclinometer/Schematics/FourSixWheels';
import ThreeAxlesSixWheels from 'views/DriverBehavior/Inclinometer/Schematics/ThreeAxlesSixWheels';
import ThreeAxlesTenWheels from 'views/DriverBehavior/Inclinometer/Schematics/ThreeAxlesTenWheels';
import { useTranslation } from 'react-i18next';

interface TireAlerts {
  leakage: number;
  lowPressure: number;
  highPressure: number;
  highTemp: number;
}

interface TireRow {
  tireCode: string;
  label: string;
  pressureDisplay: string;
  pressureUnit: string;
  pressureStatus: TireStatus;
  pressureIsStale: boolean;
  pressureTs: number | null;
  tempDisplay: string;
  tempUnit: string;
  tempStatus: TireStatus;
  tempIsStale: boolean;
  tempTs: number | null;
  alerts: TireAlerts;
}

function relativeTime(ts: number | null): string {
  if (!ts) return '';
  const diffMs = Date.now() - ts;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface AssetGroup {
  id: string;
  bumperNumber: string;
  makeModel: string;
  tireRows: TireRow[];
  hasData: boolean;
  itemPattern: string;
  pressureStatusMap: Record<string, TireStatus>;
  pressureStaleMap: Record<string, boolean>;
  tempStatusMap: Record<string, TireStatus>;
  tempStaleMap: Record<string, boolean>;
  batteryLowMap: Record<string, boolean>;
}

interface FleetDashboardViewProps {
  fleetList: any[];
  fleetData: any;
  axleWiseLimits: any[];
  unitSystem: string;
  makeModelDetails: any;
  tirePatterns: any;
  customerId: string;
  enableDarkTheme: boolean;
  fontColor: string;
  metricType: 'pressure' | 'temperature';
  batteryData?: any;
  notifications?: any[];
  leakageNotifications?: any[];
  alertFilter?: 'leakage' | 'lowPressure' | 'highPressure' | 'highTemp' | null;
}

function computeStatus(
  raw: number | null,
  ts: number | null,
  hasSensor: boolean,
  limits: { min?: number; max?: number; warning?: number },
  metricType: 'pressure' | 'temperature'
): { status: TireStatus; isStale: boolean } {
  const isStale = ts ? (Date.now() - ts) / (1000 * 60 * 60) > 24 : false;
  if (!hasSensor) return { status: TireStatus.NO_SENSOR, isStale };
  if (raw === null || ts === null)
    return { status: TireStatus.NO_DATA, isStale };
  // Use raw value for comparison (same as fleetList memo — no unit conversion on value)
  const status = determineTireStatus(
    Number(truncateTo2dp(raw)),
    limits,
    hasSensor,
    metricType
  );
  return { status, isStale };
}

const FleetDashboardView = ({
  fleetList,
  fleetData,
  axleWiseLimits,
  unitSystem,
  makeModelDetails,
  tirePatterns,
  customerId,
  enableDarkTheme,
  fontColor,
  batteryData,
  notifications = [],
  leakageNotifications = [],
  alertFilter = null,
}: FleetDashboardViewProps) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const pressureUnit = unitSystem === 'metric' ? 'KPA' : 'PSI';
  const tempUnit = unitSystem === 'metric' ? '°C' : '°F';

  const groups: AssetGroup[] = useMemo(() => {
    return fleetList.map((asset) => {
      const deviceId = asset.device?.deviceId;
      const pressureData =
        deviceId != null ? fleetData?.pressure?.[deviceId] ?? {} : {};
      const temperatureData =
        deviceId != null ? fleetData?.temperature?.[deviceId] ?? {} : {};

      const assetTireDetails = getDynamicAssetTireInfo(
        makeModelDetails,
        tirePatterns.tirePatternPositions,
        asset.make,
        asset.model,
        customerId
      );
      const itemPattern: string =
        asset.patternName ?? assetTireDetails.patternName ?? '4x4';
      const { tireCodes } = assetTireDetails;

      const allAssetTires = fleetData.assetTires ?? [];
      const assetTires = allAssetTires.filter(
        (at: any) => at.assetId === asset.assetId
      );

      const makeModelEntry = axleWiseLimits.find(
        (e: any) => e.make === asset.make && e.model === asset.model
      );
      const tireLimitsForAsset: any[] = makeModelEntry?.positions ?? [];

      const dataKeys = Object.keys(pressureData);
      const orderedKeys = getOrderedTireCodes(dataKeys, itemPattern);

      // Build per-tire alert counts for this asset (last 30 days, only ACTIVE/UNRESOLVED)
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const leakageCount: Record<string, number> = {};
      leakageNotifications
        .filter(
          (n: any) =>
            n.assetId === asset.assetId &&
            n.isActive === true &&
            new Date(n.alertStartTs).getTime() >= cutoff
        )
        .forEach((n: any) => {
          const tc: string = n.tireCode ?? '';
          if (tc) leakageCount[tc] = (leakageCount[tc] ?? 0) + 1;
        });
      const lowPressureCount: Record<string, number> = {};
      const highPressureCount: Record<string, number> = {};
      const highTempCount: Record<string, number> = {};
      notifications
        .filter(
          (n: any) =>
            n.assetId === asset.assetId &&
            new Date(n.alertStartTs).getTime() >= cutoff
        )
        .forEach((n: any) => {
          const name: string = n.rule?.ruleName?.toLowerCase() ?? '';
          const isLow = name.includes('low pressure');
          const isHigh = name.includes('high pressure');
          const isHighTemp = name.includes('temperature');
          if (!isLow && !isHigh && !isHighTemp) return;
          (n.rule?.ruleConditionAssociations ?? []).forEach((assoc: any) => {
            const parts = (assoc.tagName ?? '').split('_');
            const tc: string = parts[2] ?? '';
            if (!tc) return;
            if (isLow) lowPressureCount[tc] = (lowPressureCount[tc] ?? 0) + 1;
            if (isHigh)
              highPressureCount[tc] = (highPressureCount[tc] ?? 0) + 1;
            if (isHighTemp) highTempCount[tc] = (highTempCount[tc] ?? 0) + 1;
          });
        });

      const tireRows: TireRow[] = orderedKeys.map((key, index) => {
        const pVal = pressureData[key];
        const tVal = temperatureData[key];
        const hasSensor = !!assetTires.find((at: any) => at.tireCode === key);
        const tireLimit =
          tireLimitsForAsset.find((l: any) => l.tireCode === key) ?? {};

        // Pressure
        const pRaw: number | null = pVal?.value ?? null;
        const pTs: number | null = pVal?.ts ?? null;
        const pDisplay =
          pRaw !== null && pTs !== null ? String(Math.round(pRaw)) : 'N/A';

        // Limits converted to customer unit — matches fleetList useMemo logic exactly
        const pLimits = {
          min:
            unitSystem === 'metric'
              ? tireLimit.minPressure
              : convertKpaToPsi(tireLimit?.minPressure),
          max:
            unitSystem === 'metric'
              ? tireLimit.maxPressure
              : convertKpaToPsi(tireLimit?.maxPressure),
          warning:
            unitSystem === 'metric'
              ? tireLimit.warningPressure
              : convertKpaToPsi(tireLimit?.warningPressure),
        };
        const { status: pressureStatus, isStale: pressureIsStale } =
          computeStatus(pRaw, pTs, hasSensor, pLimits, 'pressure');

        // Temperature
        const tRaw: number | null = tVal?.value ?? null;
        const tTs: number | null = tVal?.ts ?? null;
        const tDisplay =
          tRaw !== null && tTs !== null ? String(Math.round(tRaw)) : 'N/A';

        const tLimits = {
          min:
            unitSystem === 'metric'
              ? tireLimit.minTemperature
              : convertCentToFahr(tireLimit?.minTemperature),
          max:
            unitSystem === 'metric'
              ? tireLimit.maxTemperature
              : convertCentToFahr(tireLimit?.maxTemperature),
          warning:
            unitSystem === 'metric'
              ? tireLimit.warningTemperature
              : convertCentToFahr(tireLimit?.warningTemperature),
        };
        const { status: tempStatus, isStale: tempIsStale } = computeStatus(
          tRaw,
          tTs,
          hasSensor,
          tLimits,
          'temperature'
        );

        return {
          tireCode: key,
          label: tireCodes[key] ?? `P${index + 1}`,
          pressureDisplay: pDisplay,
          pressureUnit,
          pressureStatus,
          pressureIsStale,
          pressureTs: pTs,
          tempDisplay: tDisplay,
          tempUnit,
          tempStatus,
          tempIsStale,
          tempTs: tTs,
          alerts: {
            leakage: leakageCount[key] ?? 0,
            lowPressure: lowPressureCount[key] ?? 0,
            highPressure: highPressureCount[key] ?? 0,
            highTemp: highTempCount[key] ?? 0,
          },
        };
      });

      const deviceBattery = deviceId ? batteryData?.[deviceId] : undefined;
      let batteryLowMap: Record<string, boolean> = {};
      if (itemPattern === '10x6') {
        batteryLowMap = {
          p1: !!deviceBattery?.['0x17']?.isBatteryLow,
          p2: !!deviceBattery?.['0x19']?.isBatteryLow,
          p3: !!deviceBattery?.['0x27']?.isBatteryLow,
          p4: !!deviceBattery?.['0x29']?.isBatteryLow,
          p5: !!deviceBattery?.['0x26']?.isBatteryLow,
          p6: !!deviceBattery?.['0x2A']?.isBatteryLow,
          p7: !!deviceBattery?.['0x37']?.isBatteryLow,
          p8: !!deviceBattery?.['0x39']?.isBatteryLow,
          p9: !!deviceBattery?.['0x36']?.isBatteryLow,
          p10: !!deviceBattery?.['0x3A']?.isBatteryLow,
        };
      } else if (itemPattern === '6x6') {
        batteryLowMap = {
          p1: !!deviceBattery?.['0x17']?.isBatteryLow,
          p2: !!deviceBattery?.['0x19']?.isBatteryLow,
          p3: !!deviceBattery?.['0x27']?.isBatteryLow,
          p4: !!deviceBattery?.['0x29']?.isBatteryLow,
          p5: !!deviceBattery?.['0x37']?.isBatteryLow,
          p6: !!deviceBattery?.['0x39']?.isBatteryLow,
        };
      } else if (itemPattern === '6x4') {
        batteryLowMap = {
          p1: !!deviceBattery?.['0x17']?.isBatteryLow,
          p2: !!deviceBattery?.['0x19']?.isBatteryLow,
          p3: !!deviceBattery?.['0x27']?.isBatteryLow,
          p4: !!deviceBattery?.['0x29']?.isBatteryLow,
          p5: !!deviceBattery?.['0x26']?.isBatteryLow,
          p6: !!deviceBattery?.['0x2A']?.isBatteryLow,
        };
      } else if (itemPattern === '4x6') {
        batteryLowMap = {
          p1: !!deviceBattery?.['0x16']?.isBatteryLow,
          p2: !!deviceBattery?.['0x17']?.isBatteryLow,
          p3: !!deviceBattery?.['0x19']?.isBatteryLow,
          p4: !!deviceBattery?.['0x1A']?.isBatteryLow,
          p5: !!deviceBattery?.['0x27']?.isBatteryLow,
          p6: !!deviceBattery?.['0x29']?.isBatteryLow,
        };
      } else {
        batteryLowMap = {
          p1: !!deviceBattery?.['0x17']?.isBatteryLow,
          p2: !!deviceBattery?.['0x19']?.isBatteryLow,
          p3: !!deviceBattery?.['0x27']?.isBatteryLow,
          p4: !!deviceBattery?.['0x29']?.isBatteryLow,
        };
      }

      const pressureStatusMap: Record<string, TireStatus> = {};
      const pressureStaleMap: Record<string, boolean> = {};
      const tempStatusMap: Record<string, TireStatus> = {};
      const tempStaleMap: Record<string, boolean> = {};
      tireRows.forEach((row, i) => {
        const key = `p${i + 1}`;
        pressureStatusMap[key] = row.pressureStatus;
        pressureStaleMap[key] = row.pressureIsStale;
        tempStatusMap[key] = row.tempStatus;
        tempStaleMap[key] = row.tempIsStale;
      });

      return {
        id: asset.assetId,
        bumperNumber: asset.bumperNumber,
        makeModel: `${asset.make} ${asset.model}`,
        tireRows,
        hasData: orderedKeys.length > 0,
        itemPattern,
        pressureStatusMap,
        pressureStaleMap,
        tempStatusMap,
        tempStaleMap,
        batteryLowMap,
      };
    });
  }, [
    fleetList,
    fleetData,
    axleWiseLimits,
    unitSystem,
    makeModelDetails,
    tirePatterns,
    customerId,
    pressureUnit,
    tempUnit,
    batteryData,
    notifications,
    leakageNotifications,
  ]);

  const filteredGroups = groups;

  const navbarBg = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark.navbarBg
    : clientThemePalette[CLIENT_THEME].light.navbarBg;

  const rowBorderColor = enableDarkTheme ? '#334155' : '#e2e8f0';

  const cellSx = {
    borderColor: rowBorderColor,
    fontSize: '0.75rem',
    py: 1,
    px: 1.5,
  };

  const headerCellSx = {
    ...cellSx,
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    backgroundColor: navbarBg,
    color: '#fff',
  };

  const COLUMNS: Array<{ label: string; width?: number }> = [
    { label: t('asset'), width: 280 },
    { label: t('tire_position') },
    { label: `Pressure (${pressureUnit})` },
    { label: `Temperature (${tempUnit})` },
    { label: t('alerts_30d'), width: 180 },
  ];

  return (
    <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.map(({ label, width }) => (
              <TableCell
                key={label}
                sx={{
                  ...headerCellSx,
                  ...(width ? { width, maxWidth: width } : {}),
                }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredGroups.map(
            (
              {
                id,
                bumperNumber,
                makeModel,
                tireRows,
                hasData,
                itemPattern,
                pressureStatusMap,
                pressureStaleMap,
                tempStatusMap,
                tempStaleMap,
                batteryLowMap,
              },
              groupIdx
            ) => {
              const isEven = groupIdx % 2 === 0;
              const rowBg = enableDarkTheme
                ? isEven
                  ? 'rgba(255,255,255,0.02)'
                  : 'rgba(255,255,255,0.06)'
                : isEven
                ? '#ffffff'
                : '#f1f5f9';
              const groupTopBorder = undefined;
              if (tireRows.length === 0) {
                return (
                  <TableRow key={id}>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: enableDarkTheme ? '#e2e8f0' : '#1e293b',
                        }}
                      >
                        {bumperNumber}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: enableDarkTheme ? '#64748b' : '#94a3b8',
                        }}
                      >
                        {makeModel}
                      </Typography>
                    </TableCell>
                    <TableCell
                      colSpan={4}
                      sx={{
                        ...cellSx,
                        color: enableDarkTheme ? '#64748b' : '#94a3b8',
                      }}
                    >
                      No tire data
                    </TableCell>
                  </TableRow>
                );
              }

              return tireRows.map((tire, tireIdx) => {
                const isFirst = tireIdx === 0;
                const pColor = getTpmsTireColor(
                  tire.pressureStatus,
                  tire.pressureIsStale
                );
                const tColor = getTpmsTireColor(
                  tire.tempStatus,
                  tire.tempIsStale
                );

                return (
                  <TableRow
                    key={`${id}-${tire.tireCode}`}
                    onClick={() => {
                      if (hasData) {
                        navigate(
                          `/tire-insights/vehicle-insight?asset=${bumperNumber}&metric=Pressure`
                        );
                      }
                    }}
                    sx={{
                      cursor: hasData ? 'pointer' : 'default',
                      backgroundColor: rowBg,
                      '&:hover': { opacity: 0.85 },
                      ...(isFirst && groupTopBorder
                        ? { '& td': { borderTop: groupTopBorder } }
                        : {}),
                    }}
                  >
                    {isFirst && (
                      <TableCell
                        rowSpan={tireRows.length}
                        sx={{
                          ...cellSx,
                          verticalAlign: 'top',
                          borderRight: `1px solid ${rowBorderColor}`,
                          width: 280,
                          maxWidth: 280,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: enableDarkTheme ? '#e2e8f0' : '#1e293b',
                          }}
                        >
                          {bumperNumber}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: enableDarkTheme ? '#64748b' : '#94a3b8',
                          }}
                        >
                          {makeModel}
                        </Typography>
                        <Box
                          sx={{
                            mt: 1,
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'flex-start',
                          }}
                        >
                          {[
                            {
                              label: t('pressure_3'),
                              statusMap: pressureStatusMap,
                              staleMap: pressureStaleMap,
                            },
                            {
                              label: 'Temperature',
                              statusMap: tempStatusMap,
                              staleMap: tempStaleMap,
                            },
                          ].map(({ label, statusMap, staleMap }) => (
                            <Box key={label}>
                              <Typography
                                sx={{
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  color: enableDarkTheme
                                    ? '#64748b'
                                    : '#94a3b8',
                                  mb: 0.5,
                                }}
                              >
                                {label}
                              </Typography>
                              {itemPattern === '6x4' ? (
                                <TerrainSixWheels
                                  values={{} as any}
                                  width={78}
                                  height={108}
                                  batteryLow={batteryLowMap}
                                  tireStatusMap={statusMap}
                                  tireStaleMap={staleMap}
                                />
                              ) : itemPattern === '4x6' ? (
                                <TerrainFourSixWheels
                                  values={{} as any}
                                  width={78}
                                  height={108}
                                  batteryLow={batteryLowMap}
                                  tireStatusMap={statusMap}
                                  tireStaleMap={staleMap}
                                />
                              ) : itemPattern === '6x6' ? (
                                <ThreeAxlesSixWheels
                                  values={{} as any}
                                  width={60}
                                  height={110}
                                  batteryLow={batteryLowMap}
                                  tireStatusMap={statusMap}
                                  tireStaleMap={staleMap}
                                />
                              ) : itemPattern === '10x6' ? (
                                <ThreeAxlesTenWheels
                                  values={{} as any}
                                  width={48}
                                  height={120}
                                  batteryLow={batteryLowMap}
                                  tireStatusMap={statusMap}
                                  tireStaleMap={staleMap}
                                />
                              ) : (
                                <TerrainFourWheels
                                  values={{} as any}
                                  width={60}
                                  height={108}
                                  batteryLow={batteryLowMap}
                                  tireStatusMap={statusMap}
                                  tireStaleMap={staleMap}
                                />
                              )}
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                    )}

                    <TableCell sx={{ ...cellSx, color: fontColor }}>
                      {tire.label.replace(/-/g, ' ')}
                    </TableCell>

                    <TableCell
                      sx={{ ...cellSx, color: pColor, fontWeight: 600 }}
                    >
                      {tire.pressureDisplay !== 'N/A' ? (
                        <>
                          {tire.pressureDisplay}
                          <Typography
                            component="span"
                            sx={{
                              display: 'block',
                              fontSize: '0.65rem',
                              fontWeight: 400,
                              color: enableDarkTheme ? '#64748b' : '#94a3b8',
                            }}
                          >
                            {relativeTime(tire.pressureTs)}
                          </Typography>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>

                    <TableCell
                      sx={{ ...cellSx, color: tColor, fontWeight: 600 }}
                    >
                      {tire.tempDisplay !== 'N/A' ? (
                        <>
                          {tire.tempDisplay}
                          <Typography
                            component="span"
                            sx={{
                              display: 'block',
                              fontSize: '0.65rem',
                              fontWeight: 400,
                              color: enableDarkTheme ? '#64748b' : '#94a3b8',
                            }}
                          >
                            {relativeTime(tire.tempTs)}
                          </Typography>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>

                    <TableCell sx={{ ...cellSx, py: 0.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}
                      >
                        {tire.alerts.leakage > 0 &&
                          (!alertFilter || alertFilter === 'leakage') && (
                            <Tooltip
                              title={`${tire.alerts.leakage} leakage alert(s) in last 30d`}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(239,68,68,0.2)',
                                  border: '2px solid rgba(239,68,68,0.7)',
                                  color: '#ef4444',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {tire.alerts.leakage}
                              </Box>
                            </Tooltip>
                          )}
                        {tire.alerts.lowPressure > 0 &&
                          (!alertFilter || alertFilter === 'lowPressure') && (
                            <Tooltip
                              title={`${tire.alerts.lowPressure} low pressure alert(s) in last 30d`}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(234,179,8,0.2)',
                                  border: '2px solid rgba(234,179,8,0.7)',
                                  color: '#eab308',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {tire.alerts.lowPressure}
                              </Box>
                            </Tooltip>
                          )}
                        {tire.alerts.highPressure > 0 &&
                          (!alertFilter || alertFilter === 'highPressure') && (
                            <Tooltip
                              title={`${tire.alerts.highPressure} high pressure alert(s) in last 30d`}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(248,113,113,0.2)',
                                  border: '2px solid rgba(248,113,113,0.7)',
                                  color: '#f87171',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {tire.alerts.highPressure}
                              </Box>
                            </Tooltip>
                          )}
                        {tire.alerts.highTemp > 0 &&
                          (!alertFilter || alertFilter === 'highTemp') && (
                            <Tooltip
                              title={`${tire.alerts.highTemp} high temperature alert(s) in last 30d`}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(251,146,60,0.2)',
                                  border: '2px solid rgba(251,146,60,0.7)',
                                  color: '#fb923c',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {tire.alerts.highTemp}
                              </Box>
                            </Tooltip>
                          )}
                        {tire.alerts.leakage === 0 &&
                          tire.alerts.lowPressure === 0 &&
                          tire.alerts.highPressure === 0 &&
                          tire.alerts.highTemp === 0 && (
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                color: enableDarkTheme ? '#334155' : '#cbd5e1',
                              }}
                            >
                              —
                            </Typography>
                          )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              });
            }
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FleetDashboardView;
